import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Upload, Plus, Search, MoreVertical, Pencil, Trash2,
  RefreshCw, Mail, Ban, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useSubscribers, useDeleteSubscriber, useSyncClientsToSubscribers,
  useAudiences,
} from '@/hooks/useNewsletter';
import {
  SUBSCRIBER_STATUS_LABELS,
  type SubscriberStatus, type Subscriber,
} from '@/types/newsletter';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { CsvImportModal } from '@/components/newsletter/CsvImportModal';
import { SubscriberFormModal } from '@/components/newsletter/SubscriberFormModal';
import { subscriberName } from '@/types/newsletter';

const STATUS_TONES: Record<SubscriberStatus, 'success' | 'neutral' | 'danger' | 'warning'> = {
  active:       'success',
  pending:      'warning',
  unsubscribed: 'neutral',
  bounced:      'danger',
  complained:   'danger',
};

export function SubscribersPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'newsletter', 'write') : false;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | SubscriberStatus>('');
  const [audienceId, setAudienceId] = useState('');

  const { data, isLoading, error } = useSubscribers({
    search: search || undefined,
    status: status || undefined,
    audienceId: audienceId || undefined,
    limit: 500,
  });

  const { data: audiences } = useAudiences();
  const del = useDeleteSubscriber();
  const sync = useSyncClientsToSubscribers();

  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Subscriber | null>(null);
  const [deleting, setDeleting] = useState<Subscriber | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const stats = useMemo(() => {
    if (!data) return { total: 0, active: 0, unsubscribed: 0, bounced: 0 };
    return {
      total: data.total,
      active: data.items.filter((s) => s.status === 'active').length,
      unsubscribed: data.items.filter((s) => s.status === 'unsubscribed').length,
      bounced: data.items.filter((s) => s.status === 'bounced' || s.status === 'complained').length,
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load subscribers"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function doSync() {
    try {
      const r = await sync.mutateAsync();
      toast.success(`Synced: ${r.imported} new, ${r.updated} updated from CRM clients`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Sync failed.');
    }
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(`Removed ${deleting.email}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Total"        value={stats.total.toLocaleString()} />
        <Stat label="Active"       value={stats.active.toLocaleString()} accent />
        <Stat label="Unsubscribed" value={stats.unsubscribed.toLocaleString()} />
        <Stat label="Bounced"      value={stats.bounced.toLocaleString()} alert={stats.bounced > 0} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search email, name, company…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-40">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as SubscriberStatus | '')}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'active',       label: 'Active' },
              { value: 'pending',      label: 'Pending' },
              { value: 'unsubscribed', label: 'Unsubscribed' },
              { value: 'bounced',      label: 'Bounced' },
              { value: 'complained',   label: 'Complained' },
            ]}
          />
        </div>
        <div className="w-44">
          <Select
            value={audienceId}
            onChange={(e) => setAudienceId(e.target.value)}
            options={[
              { value: '', label: 'All audiences' },
              ...(audiences ?? []).map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
        </div>
        {canWrite && (
          <>
            <Button variant="secondary" leftIcon={<RefreshCw size={13} />} onClick={doSync} loading={sync.isPending}>
              Sync Clients
            </Button>
            <Button variant="secondary" leftIcon={<Upload size={13} />} onClick={() => setImportOpen(true)}>
              Import CSV
            </Button>
            <Button leftIcon={<Plus size={13} />} onClick={() => setCreateOpen(true)}>
              Add
            </Button>
          </>
        )}
      </div>

      {/* Table */}
      {!data?.items.length ? (
        <Card>
          <EmptyState
            icon={Users}
            title={search || status || audienceId ? 'No subscribers match your filters' : 'No subscribers yet'}
            description={
              search || status || audienceId
                ? 'Try clearing some filters.'
                : 'Import a CSV, sync from your CRM clients, or add individuals manually.'
            }
            action={canWrite && !search && !status && !audienceId && (
              <div className="flex gap-3">
                <Button leftIcon={<Upload size={14} />} onClick={() => setImportOpen(true)}>
                  Import CSV
                </Button>
                <Button variant="secondary" leftIcon={<RefreshCw size={14} />} onClick={doSync} loading={sync.isPending}>
                  Sync from Clients
                </Button>
              </div>
            )}
          />
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">Email</th>
                <th className="kicker px-6 py-3 font-normal">Name</th>
                <th className="kicker px-6 py-3 font-normal">Company</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
                <th className="kicker px-6 py-3 font-normal text-right">Engagement</th>
                <th className="kicker px-6 py-3 font-normal w-12" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 group">
                  <td className="px-6 py-3">
                    <Link to={`/newsletter/subscribers/${s.id}`} className="text-text hover:text-brass-300 inline-flex items-center gap-2">
                      <Mail size={11} className="text-text-dim" />
                      {s.email}
                    </Link>
                  </td>
                  <td className="px-6 py-3 text-text-muted">
                    {subscriberName(s) !== s.email ? subscriberName(s) : <span className="text-text-dim">—</span>}
                  </td>
                  <td className="px-6 py-3 text-text-muted">
                    {s.company || <span className="text-text-dim">—</span>}
                  </td>
                  <td className="px-6 py-3">
                    <Badge tone={STATUS_TONES[s.status]}>{SUBSCRIBER_STATUS_LABELS[s.status]}</Badge>
                  </td>
                  <td className="px-6 py-3 text-right tabular text-xs text-text-muted">
                    {(s.totalSends ?? 0) > 0 ? (
                      <span>
                        <span className="text-text">{s.totalOpens ?? 0}</span> / {s.totalSends ?? 0} opens
                      </span>
                    ) : (
                      <span className="text-text-dim">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right relative">
                    {canWrite && (
                      <>
                        <button
                          onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === s.id ? null : s.id); }}
                          className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm opacity-0 group-hover:opacity-100"
                        >
                          <MoreVertical size={15} />
                        </button>
                        {openMenu === s.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                            <div className="absolute right-4 top-10 z-20 w-44 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                              <button onClick={() => { setEditing(s); setOpenMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                                <Pencil size={13} /> Edit
                              </button>
                              <div className="my-1 border-t border-border" />
                              <button onClick={() => { setDeleting(s); setOpenMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                                <Trash2 size={13} /> Delete
                              </button>
                              <button onClick={() => { setDeleting(s); setOpenMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                                <Ban size={13} /> Suppress
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <CsvImportModal open={importOpen} onClose={() => setImportOpen(false)} />
      <SubscriberFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <SubscriberFormModal open={!!editing} onClose={() => setEditing(null)} subscriber={editing} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
        loading={del.isPending}
        destructive
        title={`Delete ${deleting?.email}?`}
        confirmLabel="Delete"
        description="This removes the subscriber from all audiences. Engagement history is preserved in campaign records."
      />
    </div>
  );
}

function Stat({ label, value, accent, alert }: { label: string; value: string; accent?: boolean; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : alert ? 'text-warning' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}
